/**
 * SocialCapital.cc — dynamic article renderer.
 *
 * Reads `/sc/:slug` from the URL, looks up structured content from
 * `data/socialCapitalArticles.js`, and renders it with consistent
 * typographic treatment matching the home-page design system.
 */
import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Handshake, Sparkles, ChevronLeft } from "lucide-react";

import SocialCapitalHeader, {
  BRAND_GREEN, BRAND_BLUE, BRAND_PURPLE,
} from "@/components/socialcapital/SocialCapitalHeader";
import { getArticle, articles } from "@/data/socialCapitalArticles";


export default function SocialCapitalArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const article = getArticle(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen"
           style={{ background: "linear-gradient(180deg,#F5F8FF 0%,#F8FAFC 100%)" }}>
        <SocialCapitalHeader />
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 py-16 text-center">
          <h1 className="font-display text-3xl mb-3" style={{ color: "#0F172A" }}>
            Article not found
          </h1>
          <p className="mb-6" style={{ color: "#64748B" }}>
            Try one of these:
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {Object.keys(articles).map((s) => (
              <Link
                key={s}
                to={`/sc/${s}`}
                className="px-3 py-1.5 rounded-full text-[13px] border"
                style={{ borderColor: "#CBD5E1", color: BRAND_BLUE }}
              >
                {articles[s].eyebrow}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`sc-article-${slug}`}
      className="min-h-screen"
      style={{ background: "linear-gradient(180deg,#F5F8FF 0%,#F8FAFC 100%)" }}
    >
      <Helmet>
        <title>{`${article.title} · SocialCapital.cc`}</title>
        {article.tagline && <meta name="description" content={article.tagline} />}
      </Helmet>

      <SocialCapitalHeader />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 pt-10 pb-6">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-[12px] mb-4 opacity-70 hover:opacity-100"
          style={{ color: "#475569" }}
          data-testid="article-back"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>

        {article.eyebrow && (
          <div
            data-testid="article-eyebrow"
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-5 text-[11px] font-mono uppercase tracking-[0.18em]"
            style={{
              background: "rgba(139,92,246,0.09)",
              color: BRAND_PURPLE,
              border: "1px solid rgba(139,92,246,0.24)",
            }}
          >
            <Sparkles className="w-3 h-3" /> {article.eyebrow}
          </div>
        )}

        <h1
          data-testid="article-title"
          className="font-display text-4xl md:text-5xl lg:text-[52px] leading-[1.05] tracking-tight max-w-[900px]"
          style={{ color: "#0F172A" }}
        >
          {article.title}
        </h1>

        {article.tagline && (
          <p
            className="mt-5 text-[17px] md:text-[18px] max-w-[720px] leading-[1.55]"
            style={{ color: "#475569" }}
          >
            {article.tagline}
          </p>
        )}
      </section>

      {/* ── Body ─────────────────────────────────────────────── */}
      <article
        data-testid="article-body"
        className="max-w-[820px] mx-auto px-5 lg:px-8 pb-10"
      >
        {article.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </article>

      {/* ── CTA ──────────────────────────────────────────────── */}
      {article.cta && (
        <section className="max-w-[820px] mx-auto px-5 lg:px-8 pb-14">
          <div
            className="rounded-xl p-6 md:p-7 flex items-center gap-5 flex-wrap"
            style={{
              background: "linear-gradient(120deg,#EEF4FF 0%,#F5F0FF 100%)",
              border: "1px solid #E0E7FF",
            }}
          >
            <span
              className="inline-flex items-center justify-center w-11 h-11 rounded-full shrink-0"
              style={{ background: "white", color: BRAND_BLUE }}
            >
              <Handshake className="w-5 h-5" />
            </span>
            <p className="flex-1 min-w-[220px] text-[14.5px]" style={{ color: "#334155" }}>
              Ready to keep going?
            </p>
            <Link
              to={article.cta.to}
              data-testid="article-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13.5px] font-semibold text-white shrink-0"
              style={{ background: BRAND_BLUE }}
            >
              {article.cta.label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}


// ─── Block renderers ──────────────────────────────────────────────

function Block({ block }) {
  switch (block.t) {
    case "lead":
      return (
        <p className="text-[17px] md:text-[18px] leading-[1.65] my-5"
           style={{ color: "#0F172A" }}>{block.text}</p>
      );
    case "p":
      return (
        <p className="text-[15.5px] leading-[1.7] my-4"
           style={{ color: "#334155" }}>{block.text}</p>
      );
    case "h2":
      return (
        <h2 className="font-display text-2xl md:text-[28px] mt-10 mb-3 leading-tight"
            style={{ color: "#0F172A" }}>{block.text}</h2>
      );
    case "h3":
      return (
        <h3 className="font-display text-[20px] mt-7 mb-2 leading-tight"
            style={{ color: "#0F172A" }}>{block.text}</h3>
      );
    case "ul":
      return (
        <ul className="my-4 space-y-2 pl-1">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[15px] leading-[1.6]"
                 style={{ color: "#334155" }}>
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full mt-0.5 shrink-0"
                style={{ background: "rgba(16,185,129,0.15)", color: BRAND_GREEN }}
              >
                <Check className="w-3 h-3" />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <div className="my-6 rounded-lg border-l-4 px-5 py-4"
             style={{
               borderColor: BRAND_PURPLE,
               background: "rgba(139,92,246,0.06)",
             }}>
          <p className="text-[15.5px] leading-[1.6] italic"
             style={{ color: "#334155" }}>{block.text}</p>
        </div>
      );
    case "steps":
      return (
        <ol className="my-6 space-y-4">
          {block.items.map((s, i) => (
            <li
              key={i}
              className="grid grid-cols-[auto_1fr] gap-4 items-start rounded-lg border bg-white p-4"
              style={{ borderColor: "#E5E7EB" }}
            >
              <span
                className="inline-flex items-center justify-center min-w-[38px] h-[38px] rounded-full font-display text-[15px] shrink-0"
                style={{
                  background: "linear-gradient(135deg,#3B82F6 0%,#7C3AED 100%)",
                  color: "white",
                }}
              >
                {s.n}
              </span>
              <div>
                <div className="font-display text-[17px] mb-1 leading-tight"
                     style={{ color: "#0F172A" }}>{s.label}</div>
                <div className="text-[14.5px] leading-[1.6]"
                     style={{ color: "#475569" }}>{s.body}</div>
              </div>
            </li>
          ))}
        </ol>
      );
    case "grid": {
      const cols = block.columns === 2 ? "md:grid-cols-2"
                : block.columns === 3 ? "md:grid-cols-3"
                : "md:grid-cols-2";
      return (
        <div className={`my-6 grid gap-3 ${cols}`}>
          {block.items.map((it, i) => (
            <div
              key={i}
              className="rounded-lg border bg-white p-4 transition hover:-translate-y-0.5"
              style={{ borderColor: "#E5E7EB" }}
            >
              <div className="font-display text-[15.5px] mb-1"
                   style={{ color: "#0F172A" }}>{it.title}</div>
              <div className="text-[13.5px] leading-[1.55]"
                   style={{ color: "#475569" }}>{it.body}</div>
            </div>
          ))}
        </div>
      );
    }
    case "divider":
      return (
        <div className="my-8 flex items-center justify-center">
          <div className="h-px flex-1"
               style={{ background: "linear-gradient(90deg,transparent,#CBD5E1,transparent)" }} />
          <Sparkles className="w-4 h-4 mx-3" style={{ color: BRAND_PURPLE }} />
          <div className="h-px flex-1"
               style={{ background: "linear-gradient(90deg,transparent,#CBD5E1,transparent)" }} />
        </div>
      );
    default:
      return null;
  }
}
